import React, { FC, KeyboardEvent } from 'react';
import PropTypes from 'prop-types';
import { Pill, Root } from './Toggle.styled';

export interface ToggleProps {
  /** Controls the toggle state */
  checked: boolean;
  /** Called on state change with new state as argument */
  onChange: (newState: boolean) => void;
  /** Disables toggle */
  disabled?: boolean;
  /** Enables danger/error state */
  danger?: boolean;
  // Accessibility attributes
  tabIndex?: number;
  title?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

/**
 * Use `Toggle` to control binary state in a more eye-pleasing way than checkbox
 */
export const Toggle: FC<ToggleProps> = ({ checked, onChange, disabled = false, danger = false, tabIndex = 0, ...restProps }) => {
  const handleOnChange = () => onChange(!checked);
  const onKeyPress = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!disabled && (event.key === ' ' || event.key === 'Enter')) {
      handleOnChange();
    }
  };

  return (
    <Root
      isOn={checked}
      role="switch"
      aria-checked={checked}
      aria-disabled={disabled}
      disabled={disabled}
      danger={danger}
      onKeyPress={onKeyPress}
      onClick={disabled ? undefined : handleOnChange}
      tabIndex={tabIndex}
      {...restProps}
    >
      <Pill disabled={disabled} isOn={checked} />
    </Root>
  );
};

Toggle.propTypes = {
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  danger: PropTypes.bool,
  tabIndex: PropTypes.number,
};
